/**
 * PHU-224: map Clerk errors to user-facing copy. Clerk's `.message` /
 * `.longMessage` fields are developer-facing strings — "The verification
 * strategy is not a valid value for this account" should never reach an
 * end user. Branch on `code` (a stable identifier Clerk documents) and
 * fall back to a friendly generic.
 *
 * We sniff the error structure directly instead of relying on
 * `isClerkAPIResponseError` from `@clerk/nextjs/errors`. That helper
 * uses a brand-check that doesn't match errors thrown by
 * `@clerk/nextjs/legacy`'s `useSignIn` / `useSignUp` hooks — every
 * `.code` we care about would silently fall to the generic.
 */
interface ClerkErrorShape {
  code?: string;
  message?: string;
  longMessage?: string;
  meta?: Record<string, unknown>;
}
function extractClerkErrors(err: unknown): ClerkErrorShape[] {
  if (!err || typeof err !== "object") return [];
  // Standard ClerkAPIResponseError shape.
  const e = err as { errors?: unknown; code?: unknown };
  if (Array.isArray(e.errors)) {
    return e.errors.filter(
      (item): item is ClerkErrorShape =>
        !!item && typeof item === "object",
    );
  }
  // Some Clerk paths surface a single bare ClerkAPIError.
  if (typeof e.code === "string") return [e as ClerkErrorShape];
  return [];
}
export interface AuthErrorMessage {
  /** Headline shown to the user. */
  title: string;
  /** Optional secondary detail. */
  detail?: string;
  /** Optional next-action hint. */
  action?: {
    label: string;
    /** Internal href to navigate to, OR a sentinel handled by the caller. */
    href?: "/sign-in" | "/sign-up" | "/forgot-password";
    /** When set, the caller is expected to trigger the named flow instead
     *  of navigating. Currently only `"oauth_google"` is used (cross-strategy
     *  recovery — account exists via Google, user tried email). */
    flow?: "oauth_google";
  };
  /** Original Clerk error code, for logging / branching. */
  code?: string;
  /** True when the error is non-actionable for the user (e.g. session_exists
   *  → silently navigate). Callers should consult this before rendering. */
  silent?: boolean;
}

const FALLBACK: AuthErrorMessage = {
  title: "Something went wrong",
  detail:
    "We hit an unexpected problem signing you in. Try again — if it keeps happening, contact support.",
};

export function clerkErrorToUserMessage(err: unknown): AuthErrorMessage {
  const errors = extractClerkErrors(err);
  const first = errors[0];
  const code = first?.code;
  if (!code) return FALLBACK;

  switch (code) {
    // === Identifier / account state ===
    case "form_identifier_not_found":
      return {
        title: "We don't have an account for that email",
        detail: "Want to create one?",
        action: { label: "Sign up", href: "/sign-up" },
        code,
      };
    case "form_identifier_exists":
      return {
        title: "An account with that email already exists",
        detail: "Try logging in instead.",
        action: { label: "Log in", href: "/sign-in" },
        code,
      };

    // === Password problems ===
    case "form_password_incorrect":
    case "form_password_validation_failed":
      return {
        title: "That password doesn't match this account",
        detail: "Try again, or reset your password if you've forgotten it.",
        code,
      };
    case "form_password_pwned":
      return {
        title: "That password isn't safe to use",
        detail:
          "It has appeared in a data breach. Please choose a different one.",
        code,
      };
    case "form_password_length_too_short":
      return {
        title: "Password is too short",
        detail: "Use at least 8 characters.",
        code,
      };

    // === Cross-strategy (the headline bug behind PHU-224) ===
    case "strategy_for_user_invalid":
    case "verification_strategy_invalid":
    case "form_param_strategy_invalid":
      return {
        title: "That email is registered with a different sign-in method",
        detail: "Try \"Continue with Google\" instead.",
        action: { label: "Continue with Google", flow: "oauth_google" },
        code,
      };

    // === Verification code problems ===
    case "form_code_incorrect":
    case "verification_failed":
      return {
        title: "That code didn't work",
        detail: "Double-check the email we sent — codes expire after 10 minutes.",
        code,
      };
    case "verification_expired":
      return {
        title: "That code has expired",
        detail: "Request a fresh one and try again.",
        code,
      };

    // === Session lifecycle — not really errors ===
    case "session_exists":
      return {
        title: "You're already signed in",
        silent: true,
        code,
      };
    case "client_state_invalid":
      return {
        title: "Your session expired",
        detail: "Sign in again to continue.",
        code,
      };

    // === OAuth-flow errors ===
    case "oauth_access_denied":
      return {
        title: "Google sign-in was cancelled",
        detail: "Try again, or use your email and password.",
        code,
      };
    case "oauth_callback_invalid":
    case "oauth_email_domain_reserved_by_saml":
    case "external_account_not_found":
      return {
        title: "Google sign-in didn't complete",
        detail: "Try again, or use email and password.",
        code,
      };

    // === Rate limiting ===
    case "too_many_requests":
      return {
        title: "Too many attempts — please wait a minute",
        detail: "We've temporarily slowed sign-in to protect this account.",
        code,
      };
  }

  return { ...FALLBACK, code };
}
